import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { makeStyles } from "@material-ui/core/styles";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import React, { FunctionComponent, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { useField, useFormikContext } from "formik";
import FormikInputField from "./../form/FormikInputField";

export interface SimpleCell {
  type: "numberField" | "stringField" | "dateField";
  name: string;
  suffix?: string;
  disabled?: boolean;
  value?: any;
  header?: string;
  minDate?: Date;
  maxDate?: Date;
}

interface FormTableProps {
  headers: Array<string>;
  rows: Array<Array<SimpleCell | string>>;
  disabled?: boolean;
}

const useStyles = makeStyles({
  tableContainer: {
    boxShadow: "none",

    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
    "&::-webkit-scrollbar": {
      height: "10px",
      backgroundColor: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#DCDEE2",
    },
  },
  inputBorderStyles: {
    border: "none",
    outline: "none",
  },
  inputStyles: {
    boxShadow: "none",
    width: "140px",
    height: "20px",
    paddingRight: "10px",
  },
  disabledcell: {
    backgroundColor: "rgb(244, 244, 244,.55)",
  },
  hoverEffect: {
    "&:hover": {
      border: "1px solid black !important",
    },
  },
  table: {
    minWidth: 650,
    paddingBottom: "1px",

    "& td": {
      border: "1px solid transparent",
      borderBottom: "1px solid #DCDEE2",
    },
    "& th": {
      fontWeight: "bold",
      fontSize: "larger",
    },
    "& td, th": {
      color: "#6b7280",
      minWidth: "165px",
    },
    "& th:nth-child(even), td:nth-child(even)": {
      borderLeft: "1px solid #DCDEE2",
      borderRight: "1px solid #DCDEE2",
    },
    "& th:first-child, td:first-child": {
      fontWeight: "bold",
      borderBottom: "1px solid #DCDEE2",
    },
    "& thead>tr>th:first-child": {
      borderBottom: "none",
    },

    "& thead th": {
      position: "sticky",
      insetInlineStart: "0",
      left: "unset",
      right: "unset",
      zIndex: 0,
      background: "#f4f4f4",
    },
    "& thead th:first-child": {
      position: "sticky",
      insetInlineStart: "0",
      left: "unset",
      right: "unset",
      zIndex: 1,
    },
    "& tbody th": {
      position: "sticky",
      insetInlineStart: "0",
      left: "unset",
      right: "unset",
      background: "white",
      zIndex: 1,
    },
    "& input": {
      border: "none",
      // borderBottom: '1px solid #DCDEE2'
    },
    "& .react-datepicker__input-container input": {
      textAlign: "center",
      height: "28.25px",
    },
    "& .formTableInput": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      textAlign: "center",
      alignContent: "center",
      fontSize: "larger",
      margin: "auto",
      "& div": {
        alignItems: "center",
      },
      "& p": {
        display: "none",
      },
      "& p.MuiTypography-root ": {
        display: "block",
      },
      "& input": {
        fontSize: "larger",
      },
      "& input[type=number]": {
        "-moz-appearance": "textfield",
        textAlign: "center !important",
      },
      "& input[type=number]::-webkit-outer-spin-button": {
        "-webkit-appearance": "none",
        margin: 0,
      },
      "& input[type=number]::-webkit-inner-spin-button": {
        "-webkit-appearance": "none",
        margin: 0,
      },
    },
  },
});

function DateFieldCellUI(props: { cell: SimpleCell }) {
  const [field, meta] = useField(props.cell);
  const { setFieldValue } = useFormikContext();
  const classes = useStyles();
  const [isActive, setisActive] = useState(false);
  return (
    <TableCell
      align="center"
      className={!props.cell.disabled ? classes.hoverEffect : ""}
      style={
        isActive
          ? {
              border: "1px solid black",
            }
          : {}
      }
    >
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div style={{ width: "200px" }}>
          {props.cell.disabled ? (
            <DatePicker
              selected={props.cell.value}
              {...field}
              dateFormat={"yyyy-MM-dd"}
              disabled={props.cell.disabled}
            />
          ) : (
            <DatePicker
              className={classes.inputBorderStyles}
              placeholderText="YYYY-MM-DD"
              minDate={props.cell.minDate || new Date("2010")}
              maxDate={props.cell.maxDate || new Date("2040")}
              dateFormat={"yyyy-MM-dd"}
              selected={(field.value && new Date(field.value)) || null}
              {...field}
              onChange={(value: any) => {
                if (value === null) {
                  setFieldValue(field.name, null);
                  return;
                }
                const UTCDate = Date.UTC(
                  value.getFullYear(),
                  value.getMonth(),
                  value.getDate()
                );
                const fixedDate = new Date(UTCDate);
                const year = fixedDate.getUTCFullYear();
                const month = ("0" + (fixedDate.getMonth() + 1)).slice(-2);
                const day = ("0" + fixedDate.getUTCDate()).slice(-2);
                const newDate = `${year}-${month}-${day}`;
                setFieldValue(field.name, newDate);
              }}
              disabled={props.cell.disabled}
              onCalendarOpen={() => setisActive(true)}
              onCalendarClose={() => setisActive(false)}
            />
          )}
        </div>
      </div>
    </TableCell>
  );
}

function StringFieldCellUI(props: { cell: SimpleCell }) {
  const [field, meta] = useField(props.cell);
  const classes = useStyles();
  const [isActive, setisActive] = useState(false);
  const rtl = useAppContext((ctx) => ctx.rtl);
  const direction = rtl ? "rtl" : "ltr";
  return (
    <TableCell
      align="center"
      className={!props.cell.disabled ? classes.hoverEffect : ""}
      style={
        isActive
          ? {
              border: "1px solid black",
            }
          : {}
      }
    >
      <div className="formTableInput">
        {props.cell.disabled ? (
          <div className={classes.inputStyles}>{props.cell.value}</div>
        ) : (
          <FormikInputField
            borderStyles={classes.inputBorderStyles}
            className={classes.inputStyles}
            {...field}
            dir={direction}
            value={props.cell.value}
            disabled={props.cell.disabled}
            cell={props.cell}
            onFocus={() => setisActive(true)}
            onBlur={() => setisActive(false)}
            inTable
          />
        )}
        {props.cell.suffix}
      </div>
    </TableCell>
  );
}

function NumberFieldCellUI(props: { cell: SimpleCell }) {
  const [field, meta] = useField(props.cell);
  const classes = useStyles();
  const [isActive, setisActive] = useState(false);
  const rtl = useAppContext((ctx) => ctx.rtl);
  const direction = rtl ? "rtl" : "ltr";

  return (
    <TableCell
      align="center"
      className={
        !props.cell.disabled ? classes.hoverEffect : classes.disabledcell
      }
      style={
        isActive
          ? {
              border: "1px solid black",
            }
          : {}
      }
    >
      <div className="formTableInput">
        {props.cell.disabled ? (
          <div style={{ width: "100%", justifyContent: "center" }}>
            <div>
              {field.value && typeof field.value !== "object"
                ? field.value
                : props.cell.value}{" "}
              {props.cell.suffix}
            </div>
          </div>
        ) : (
          <FormikInputField
            suffix={props.cell.suffix}
            borderStyles={classes.inputBorderStyles}
            className={classes.inputStyles}
            type="number"
            cell={props.cell}
            label=""
            dir={direction}
            {...field}
            value={props.cell.value}
            disabled={props.cell.disabled}
            onFocus={() => setisActive(true)}
            onBlur={() => setisActive(false)}
            inTable
          />
        )}

        {/* {props.cell.suffix} */}
      </div>
    </TableCell>
  );
}

function FormTableCellUI(props: {
  disabled?: boolean | undefined;
  colIndex: number;
  cell: SimpleCell | string;
}) {
  const cell = props.cell as SimpleCell;
  const components: { [name: string]: JSX.Element } = {
    stringField: (
      <StringFieldCellUI
        cell={{ ...cell, disabled: cell.disabled || props.disabled }}
      />
    ),
    dateField: (
      <DateFieldCellUI
        cell={{ ...cell, disabled: cell.disabled || props.disabled }}
      />
    ),
    numberField: (
      <NumberFieldCellUI
        cell={{ ...cell, disabled: cell.disabled || props.disabled }}
      />
    ),
    text: <TableCell align="center">{props.cell}</TableCell>,
  };

  if (props.cell instanceof String) {
    return components.text;
  }

  return components[(props.cell as SimpleCell).type];
}

const SimpleFormTable: FunctionComponent<FormTableProps> = (
  props: FormTableProps
) => {
  const classes = useStyles();
  const ctx = useAppContext();

  return (
    <>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table className={classes.table} stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align={ctx.rtl ? "right" : "left"}>
                {props.headers[0]}
              </TableCell>
              {props.headers.slice(1).map((h) => (
                <TableCell align="center">{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {props.rows.map((row, idx) => (
              <TableRow key={`${idx}_row1`}>
                <TableCell
                  component="th"
                  scope="row"
                  align={ctx.rtl ? "right" : "left"}
                >
                  {row[0]}
                </TableCell>
                {row.slice(1).map((cell, cellIdx) => (
                  <FormTableCellUI
                    key={`${idx} ${cellIdx}_cell`}
                    colIndex={cellIdx}
                    cell={cell}
                    {...props}
                  />
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
export default SimpleFormTable;
